/**
 * Client mirror of the server's ability clocks and progression.
 *
 * `S2C.Progress` arrives on change, not every tick, so cooldowns are *interpolated*: each
 * message reseeds the six clocks and the frame loop counts them down locally. A local
 * press seeds its own clock optimistically so the sweep starts on the press frame; the
 * next Progress corrects it either way (including a refusal, which snaps the cell back to
 * ready).
 *
 * Six slots since protocol v3, not three (§3.8). Slots 3..5 resolve to `undefined` from
 * `abilityBySlot` until Phase 2b authors them, and every reader here has to tolerate that —
 * an empty cell is a cell that draws empty, never one that throws.
 */

import {
  CALLINGS,
  GLOBAL_COOLDOWN_MS,
  ResourceKind,
  resourceOf,
  type AbilityDef,
  type ProgressMsg,
} from '@stillsun/shared';

/** How many ability slots the bar and the wire carry. Keys 1-6. */
export const ABILITY_SLOTS = 6;
const SLOTS = [0, 1, 2, 3, 4, 5] as const;

const clampSlot = (n: number): number =>
  n < 0 ? 0 : n >= ABILITY_SLOTS ? ABILITY_SLOTS - 1 : n | 0;

export interface ProgressChange {
  /** The new level when this message crossed one, else null. */
  leveledUpTo: number | null;
  /** True when the Calling changed (the HUD bar has to rebuild its glyphs). */
  callingChanged: boolean;
}

export class AbilityTracker {
  xp = 0;
  level = 1;
  skillPoints = 0;
  calling = 0;
  /**
   * Vanguard rage (0..100) or Warden mana, straight off `S2C.Progress` (v3.1 §9.1). Unlike
   * the clocks below it is **never** counted down locally: a cooldown is a clock and can be
   * interpolated, but a resource is a balance and only the server knows what it is. Draining
   * mana between messages would dim a cell the player can in fact afford.
   */
  resource = 0;
  resourceMax = 0;
  /** True once a Progress message has ever arrived. */
  seen = false;

  /** Remaining cooldown per slot in ms, counted down locally between messages. */
  private readonly cd = new Float64Array(ABILITY_SLOTS);
  /** Full cooldown per slot, for the sweep denominator. */
  private readonly span = new Float64Array(ABILITY_SLOTS).fill(1);
  /** Remaining global cooldown in ms, counted down the same way. */
  private gcd = 0;
  /** Milliseconds left on the local player's cast; 0 = not casting (v3.1 §9.1). */
  private cast = 0;
  /** The slot that cast belongs to, and the span it started from — the bar's denominator. */
  private castSlotId = 0;
  private castSpan = 1;

  /**
   * The Calling's slot table. Sparse by design: `undefined` in slots 3..5 until Phase 2b.
   * Always `ABILITY_SLOTS` long so a `.map` over it lays out the whole bar.
   */
  get defs(): readonly (AbilityDef | undefined)[] | null {
    const list = CALLINGS[this.calling]?.abilities;
    if (!list) return null;
    return Array.from({ length: ABILITY_SLOTS }, (_, i) => list[i]);
  }

  def(slot: number): AbilityDef | undefined {
    return CALLINGS[this.calling]?.abilities[clampSlot(slot)];
  }

  /** Milliseconds left on the global cooldown; 0 = ready. */
  get gcdMs(): number {
    return this.gcd;
  }

  /** 0 = ready, 1 = just pressed. The GCD's own sweep, which is not a cooldown. */
  get gcdFraction(): number {
    if (this.gcd <= 0) return 0;
    return Math.min(1, this.gcd / GLOBAL_COOLDOWN_MS);
  }

  /** Milliseconds left on the local player's cast; 0 = not casting. */
  get castMs(): number {
    return this.cast;
  }

  /** Which slot is filling. Meaningless while `castMs` is 0, exactly as on the wire. */
  get castSlot(): number {
    return this.castSlotId;
  }

  /** 0 at the start of a cast, 1 as it lands. Drives the cast bar's fill. */
  get castFraction(): number {
    if (this.cast <= 0) return 0;
    return Math.max(0, Math.min(1, 1 - this.cast / (this.castSpan || 1)));
  }

  /** The ability the cast bar is named after, or `undefined` when nothing is casting. */
  get castDef(): AbilityDef | undefined {
    return this.cast > 0 ? this.def(this.castSlotId) : undefined;
  }

  /** Which resource the Calling spends, so the bar knows whether to draw one and in what. */
  get resourceKind(): ResourceKind {
    return resourceOf(this.calling);
  }

  /** True when the player cannot pay for this slot. An affordable-but-cooling slot is false. */
  short(slot: number): boolean {
    const def = this.def(slot);
    if (!def) return false;
    const cost = def.resourceCost ?? 0;
    return cost > 0 && this.resource < cost;
  }

  /**
   * Everything that stands between a key press and a cast: an ability in the slot, its own
   * cooldown, the global cooldown it respects (`def.gcd !== false`, the documented read), and
   * enough rage or mana to pay for it. The bar dims what this refuses, so a Vanguard at 5 rage
   * can see that Strike is not going to happen without pressing it to find out.
   */
  ready(slot: number): boolean {
    const s = clampSlot(slot);
    const def = this.def(s);
    if (!def) return false;
    if ((this.cd[s] ?? 0) > 0) return false;
    if (def.gcd !== false && this.gcd > 0) return false;
    return !this.short(s);
  }

  onProgress(msg: ProgressMsg): ProgressChange {
    const leveledUpTo = this.seen && msg.level > this.level ? msg.level : null;
    const callingChanged = !this.seen || msg.calling !== this.calling;
    this.seen = true;
    this.xp = msg.xp;
    this.level = msg.level;
    this.skillPoints = msg.skillPoints;
    this.calling = msg.calling;
    this.resource = msg.resource;
    this.resourceMax = msg.resourceMax;
    this.gcd = Math.max(0, msg.gcdMs);
    // Assigned, never max()'d against the local clock: a server-sent 0 is an *interrupt*
    // (moved, or took a hit) and has to win, or the bar keeps filling after the cast died.
    const cast = Math.max(0, msg.castMs);
    if (cast > 0) {
      this.castSlotId = clampSlot(msg.castSlot);
      // The wire carries time remaining, so the largest remaining seen since the bar was
      // empty is the best denominator there is — the same trick the cooldown spans use.
      const nominal = Math.max(1, this.def(this.castSlotId)?.castMs ?? cast);
      this.castSpan = this.cast > 0 ? Math.max(this.castSpan, cast) : Math.max(nominal, cast);
    }
    this.cast = cast;
    for (const i of SLOTS) {
      const ms = Math.max(0, msg.cooldownsMs[i]);
      this.cd[i] = ms;
      // The wire carries time *remaining*, never the span. The largest remaining seen
      // since the clock last hit zero is therefore the best denominator available — it
      // tracks a skill node that shortened the real cooldown without any extra protocol.
      const nominal = Math.max(1, this.def(i)?.cooldownMs ?? 1);
      this.span[i] = ms === 0 ? nominal : Math.max(this.span[i] ?? 1, ms);
    }
    return { leveledUpTo, callingChanged };
  }

  /**
   * Local optimistic cast: starts the sweep, and the cast bar, on the press frame.
   *
   * Progress arrives on change, which for a 2 s Mend is a beat after the key — long enough
   * for a player to press it twice. The bar the press puts up is corrected (or cleared) by
   * the next message either way.
   *
   * Refusing here costs nothing but the local sweep: input.ts has already latched the button
   * bit, and the server decides. So a stale resource reading (Progress is on change, not on a
   * tick) can only ever mean the sweep starts a message late, never that a cast is lost.
   */
  press(slot: number): boolean {
    const s = clampSlot(slot);
    // `ready` already proves there is a def here; the second check is for the narrowing.
    const def = this.def(s);
    if (!this.ready(s) || !def) return false;
    this.cd[s] = def.cooldownMs;
    this.span[s] = Math.max(1, def.cooldownMs);
    if (def.gcd !== false) this.gcd = GLOBAL_COOLDOWN_MS;
    const castMs = def.castMs ?? 0;
    if (castMs > 0) {
      this.cast = castMs;
      this.castSpan = castMs;
      this.castSlotId = s;
    }
    return true;
  }

  /**
   * `dtMs` is the frame delta in milliseconds. Every clock here is counted down at frame rate
   * rather than at the ~1 Hz the server sends Progress at, so a sweep moves smoothly instead
   * of stepping. The resource is deliberately not among them (see the field).
   */
  tick(dtMs: number): void {
    for (const i of SLOTS) {
      const rem = this.cd[i] ?? 0;
      if (rem > 0) this.cd[i] = Math.max(0, rem - dtMs);
    }
    if (this.gcd > 0) this.gcd = Math.max(0, this.gcd - dtMs);
    if (this.cast > 0) this.cast = Math.max(0, this.cast - dtMs);
  }

  remainingMs(slot: number): number {
    return this.cd[clampSlot(slot)] ?? 0;
  }

  /** 0 = ready, 1 = just cast. Drives the conic sweep. */
  fraction(slot: number): number {
    const s = clampSlot(slot);
    const rem = this.cd[s] ?? 0;
    if (rem <= 0) return 0;
    return Math.min(1, rem / (this.span[s] || 1));
  }

  /**
   * Back to "we know nothing", called when a new Welcome lands. `seen` and `calling` have to
   * go with the clocks: leaving `seen` true would disable the fallback that reads the class
   * out of `PlayerSnap.look`, so a player who switched Calling in the hub would stare at the
   * old glyphs until a Progress message arrived.
   */
  reset(): void {
    this.cd.fill(0);
    this.span.fill(1);
    this.gcd = 0;
    this.cast = 0;
    this.castSpan = 1;
    this.castSlotId = 0;
    this.seen = false;
    this.calling = 0;
    this.skillPoints = 0;
    this.resource = 0;
    this.resourceMax = 0;
  }
}
